
import React, { ReactNode } from 'react';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { SubscriptionFeatures } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePlanSelectDialog } from "@/contexts/PlanSelectDialogContext";
import { FeaturePreview } from './FeaturePreview';
import { ContractServicesPreview } from './feature-previews/ContractServicesPreview';
import { PaymentTypePreview } from './feature-previews/PaymentTypePreview';
import { RemindersPreview } from './feature-previews/RemindersPreview';
import { useTranslation } from 'react-i18next';

interface FeatureGuardProps {
  feature: keyof SubscriptionFeatures;
  children: ReactNode;
  fallback?: ReactNode;
  inline?: boolean;
  showPreview?: boolean;
  className?: string;
}

export function FeatureGuard({ 
  feature, 
  children, 
  fallback, 
  inline = false,
  showPreview = true,
  className
}: FeatureGuardProps) {
  const { hasFeature, isLoading } = useFeatureFlags();
  const { openDialog } = usePlanSelectDialog();
  const { t } = useTranslation();
  
  const getRelatedFeatures = (featureKey: keyof SubscriptionFeatures): string[] => {
    const featureMap: Record<string, string[]> = {
      'currency_conversion': ['Price Comparison', 'Currency Conversion'],
      'price_comparison': ['Price Comparison', 'Currency Conversion'],
      'grouped_contracts': ['Grouped Contracts'],
      'dashboard': ['Dashboard Analytics']
    };
    return featureMap[featureKey] || [formatFeatureName(featureKey)];
  };
  
  const inlineFallback = (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center cursor-help">
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-white dark:bg-gray-900 p-3 max-w-[300px] shadow-lg border border-amber-200 dark:border-amber-800">
          <div className="space-y-3">
            <div className="flex items-center gap-2 font-medium">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <h4>{t('featureNotAvailable', 'Feature Not Available')}</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('currentPlanLimitation', 'Your current plan doesn\'t include:')}
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground">
              {getRelatedFeatures(feature).map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
            <Button 
              size="sm"
              onClick={openDialog}
              className="w-full"
            >
              {t('viewPlans', 'View Plans')}
            </Button>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const defaultFallback = (
    <Card className="p-6 flex flex-col items-center justify-center text-center space-y-4 bg-yellow-50 dark:bg-yellow-900/20">
      <AlertCircle className="h-10 w-10 text-amber-500" />
      <h3 className="text-lg font-semibold">{t('featureNotAvailable', 'Feature Not Available')}</h3>
      <div className="space-y-2">
        <p className="text-muted-foreground max-w-md">
          {t('subscriptionRequirement', 'This feature requires a subscription plan that includes:')}
        </p>
        <ul className="list-disc list-inside text-muted-foreground">
          {getRelatedFeatures(feature).map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      </div>
      <Button 
        onClick={openDialog} 
        variant="outline" 
        className="mt-4"
      >
        {t('viewSubscriptionPlans', 'View Subscription Plans')}
      </Button>
    </Card>
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!hasFeature(feature)) {
    // If a custom fallback is provided, use that first
    if (fallback) {
      return <div className={className}>{fallback}</div>;
    }
    
    if (inline) {
      return inlineFallback;
    }

    if (showPreview) {
      switch (feature) {
        case 'contract_services':
          return <div className={className}><ContractServicesPreview /></div>;
        case 'payment_type_selection':
          return <div className={className}><PaymentTypePreview /></div>;
        case 'reminders':
          return <div className={className}><RemindersPreview /></div>;
        default:
          return (
            <div className={className}>
              <FeaturePreview
                feature={feature}
                title={formatFeatureName(feature)}
                description={`${t('upgradeToAccessFeature', 'Upgrade your plan to access')} ${formatFeatureName(feature).toLowerCase()} ${t('andOtherFeatures', 'and other premium features.')}`}
              />
            </div>
          );
      }
    }

    return <div className={className}>{defaultFallback}</div>;
  }

  return <div className={className}>{children}</div>;
}

function formatFeatureName(feature: keyof SubscriptionFeatures): string {
  const displayNames: Record<keyof SubscriptionFeatures, string> = {
    currency_conversion: "Currency Conversion",
    multiple_currencies: "Multiple Currencies",
    price_comparison: "Price Comparison",
    grouped_contracts: "Grouped Contracts",
    contract_services: "Contract Services",
    reminders: "Reminders",
    payment_type_selection: "Payment Type Selection",
    dashboard: "Dashboard Analytics",
    contract_pdf_email: "Contract PDF Email"
  };
  
  return displayNames[feature] || feature;
}
